import { logger } from '@/lib/telemetry/logger';
import type { AnalyticsProvider } from '../types';

type SnapCommand = [command: string, name: string, properties?: Record<string, unknown>];
interface SnapTracker {
  (...args: SnapCommand): void;
  queue?: SnapCommand[];
  handleRequest?: (...args: SnapCommand) => void;
}

declare global {
  interface Window {
    snaptr?: SnapTracker;
  }
}

// Only the production-gated root component installs the vendor queue.
export function initializeSnapchatPixel(): void {
  if (typeof window === 'undefined' || window.snaptr !== undefined) return;
  const tracker: SnapTracker = (...args: SnapCommand): void => {
    if (tracker.handleRequest !== undefined) {
      tracker.handleRequest.apply(tracker, args);
    } else {
      tracker.queue = [...(tracker.queue ?? []), args];
    }
  };
  tracker.queue = [];
  window.snaptr = tracker;
  tracker('init', 'cba86c89-b43d-4ff9-81c9-619a204a7154', {});
}

export function createSnapchatProvider(): AnalyticsProvider {
  return {
    async initialize(): Promise<void> {},
    checkGate: (): boolean => false,
    getConfig: (): Record<string, unknown> => ({}),
    getExperiment: (): { value: null; ruleID: string } => ({ value: null, ruleID: '' }),
    identifyUser: (): void => {},
    shutdown: (): void => {},
    trackEvent(name: string, properties: Record<string, unknown>): void {
      if (typeof window === 'undefined' || window.snaptr === undefined) return;
      if (name === 'auth_method_used' && properties.isNewUser === true) {
        window.snaptr('track', 'SIGN_UP', { sign_up_method: properties.method });
      }
      if (
        name === 'trade_success' &&
        properties.instruction === 'buy' &&
        properties.network === 'mainnet'
      ) {
        trackPurchase(properties);
      }
    },
  };
}

function trackPurchase(properties: Record<string, unknown>): void {
  const { tradeNotionalD6, signature, marketAddress, outcomeIndex } = properties;
  if (
    typeof tradeNotionalD6 !== 'string' ||
    !/^\d+$/.test(tradeNotionalD6) ||
    BigInt(tradeNotionalD6) <= BigInt(0) ||
    typeof signature !== 'string' ||
    signature === '' ||
    typeof marketAddress !== 'string' ||
    typeof outcomeIndex !== 'number'
  ) {
    logger.warn('Snapchat purchase skipped: incomplete trade attribution', {
      eventName: 'trade_success',
    });
    return;
  }
  // USDC D6 -> USD decimal string at the vendor boundary, without float rounding.
  const digits = BigInt(tradeNotionalD6).toString().padStart(7, '0');
  window.snaptr?.('track', 'PURCHASE', {
    price: `${digits.slice(0, -6)}.${digits.slice(-6)}`,
    currency: 'USD',
    transaction_id: signature,
    item_ids: [`${marketAddress}:${outcomeIndex}`],
    number_items: 1,
  });
}
